import React, { useMemo } from "react";
import {
  Box,
  Card,
  Grid,
  Skeleton,
  Typography,
  Chip,
  LinearProgress,
  useTheme,
  useMediaQuery,
  Stack,
  Divider,
} from "@material-ui/core";
import {
  TrendingUp,
  TrendingDown,
  TrendingFlat,
  Assignment,
  CheckCircle,
} from "@material-ui/icons";
import { fNumber, fPercent } from "src/utils/formatNumber";
import Chart from "react-apexcharts";
import useChart from "../charts/use-chart";

export default function ProductividadEmpleado({ data, loading }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // Configuración del gráfico de tendencias
  const chartOptions = useChart({
    chart: {
      type: "line",
      toolbar: { show: false },
      zoom: { enabled: false },
    },
    stroke: {
      curve: "smooth",
      width: 3,
    },
    colors: ["#2196f3"],
    xaxis: {
      categories: data?.tendenciasSemanales?.map((item) => item.semana) || [],
      labels: {
        style: {
          fontSize: isMobile ? "10px" : "12px",
        },
      },
    },
    yaxis: {
      labels: {
        style: {
          fontSize: isMobile ? "10px" : "12px",
        },
      },
    },
    tooltip: {
      y: {
        formatter: (value) => `${value} tareas`,
      },
    },
    grid: {
      strokeDashArray: 3,
    },
  });

  const series = [
    {
      name: "Tareas Completadas",
      data: data?.tendenciasSemanales?.map((item) => item.cantidad) || [],
    },
  ];

  // Procesar datos de distribución por tipo
  const distribucionData = useMemo(() => {
    if (!data?.distribucionTipoTarea?.length) return { series: [], labels: [] };

    const sortedData = [...data.distribucionTipoTarea]
      .sort((a, b) => b.cantidad - a.cantidad)
      .slice(0, 8);

    return {
      series: sortedData.map((item) => item.cantidad),
      labels: sortedData.map((item) => item.tipo),
    };
  }, [data]);

  // Configuración del gráfico donut
  const donutOptions = useChart({
    chart: {
      type: "donut",
    },
    labels: distribucionData.labels,
    legend: {
      position: "bottom",
      horizontalAlign: "center",
      fontSize: isMobile ? "11px" : "13px",
    },
    tooltip: {
      y: {
        formatter: (value, { seriesIndex }) => {
          const total = distribucionData.series.reduce((a, b) => a + b, 0);
          const percentage = total > 0 ? ((value / total) * 100).toFixed(1) : 0;
          return `${value} tareas (${percentage}%)`;
        },
      },
    },
    plotOptions: {
      pie: {
        donut: {
          size: "65%",
          labels: {
            show: true,
            name: {
              fontSize: isMobile ? "14px" : "16px",
            },
            value: {
              fontSize: isMobile ? "20px" : "24px",
              formatter: (val) => fNumber(val),
            },
            total: {
              show: true,
              label: "Total",
              fontSize: isMobile ? "12px" : "14px",
              formatter: () => {
                const total = distribucionData.series.reduce(
                  (a, b) => a + b,
                  0,
                );
                return fNumber(total);
              },
            },
          },
        },
      },
    },
  });

  const getTrendIcon = (change) => {
    if (change > 0)
      return (
        <TrendingUp sx={{ color: "#4caf50", fontSize: isMobile ? 20 : 24 }} />
      );
    if (change < 0)
      return (
        <TrendingDown sx={{ color: "#f44336", fontSize: isMobile ? 20 : 24 }} />
      );
    return (
      <TrendingFlat sx={{ color: "#ff9800", fontSize: isMobile ? 20 : 24 }} />
    );
  };

  const getTrendColor = (change) => {
    if (change > 0) return "#4caf50";
    if (change < 0) return "#f44336";
    return "#ff9800";
  };

  if (loading) {
    return (
      <Card sx={{ p: { xs: 2, sm: 3 }, height: "100%" }}>
        <Skeleton variant="text" width="60%" height={32} sx={{ mb: 2 }} />
        <Skeleton variant="rectangular" height={300} />
      </Card>
    );
  }

  const { actual = 0, anterior = 0, porcentajeCambio = 0 } = data || {};

  return (
    <Card sx={{ p: { xs: 2, sm: 3 }, height: "100%" }}>
      <Typography
        variant={isMobile ? "subtitle1" : "h6"}
        gutterBottom
        fontWeight="bold"
      >
        Productividad
      </Typography>
      <Divider sx={{ mb: 2 }} />

      <Grid container spacing={2}>
        {/* Columna izquierda - Métricas */}
        <Grid item xs={12} lg={5}>
          <Stack spacing={2} sx={{ height: "100%" }}>
            {/* Métrica principal */}
            <Box
              sx={{
                textAlign: "center",
                p: 2,
                bgcolor: "primary.lighter",
                borderRadius: 2,
              }}
            >
              <Box
                display="flex"
                alignItems="center"
                justifyContent="center"
                mb={1}
              >
                <CheckCircle
                  sx={{
                    color: "primary.main",
                    mr: 1,
                    fontSize: isMobile ? 20 : 24,
                  }}
                />
                <Typography
                  variant={isMobile ? "h5" : "h4"}
                  color="primary.main"
                  fontWeight="bold"
                >
                  {fNumber(actual)}
                </Typography>
              </Box>
              <Typography
                variant="body2"
                color="textSecondary"
                fontWeight="medium"
              >
                Tareas Completadas
              </Typography>
              <Typography variant="caption" color="textSecondary">
                Período actual
              </Typography>
            </Box>

            {/* Comparativa con período anterior */}
            <Box
              sx={{
                p: 2,
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 2,
              }}
            >
              <Stack spacing={1}>
                <Box display="flex" alignItems="center" justifyContent="center">
                  {getTrendIcon(porcentajeCambio)}
                  <Typography
                    variant={isMobile ? "h6" : "h5"}
                    sx={{ color: getTrendColor(porcentajeCambio), ml: 1 }}
                    fontWeight="bold"
                  >
                    {porcentajeCambio > 0 ? "+" : ""}
                    {fPercent(porcentajeCambio)}
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={Math.min(Math.abs(porcentajeCambio), 100)}
                  sx={{
                    height: 8,
                    borderRadius: 4,
                    bgcolor: "grey.200",
                    "& .MuiLinearProgress-bar": {
                      bgcolor: getTrendColor(porcentajeCambio),
                      borderRadius: 4,
                    },
                  }}
                />
                <Typography
                  variant="body2"
                  color="textSecondary"
                  textAlign="center"
                  fontWeight="medium"
                >
                  vs período anterior
                </Typography>
                <Typography
                  variant="caption"
                  color="textSecondary"
                  textAlign="center"
                >
                  ({fNumber(anterior)} tareas)
                </Typography>
              </Stack>
            </Box>

            {/* Distribución por tipo (lista) */}
            {!isMobile && data?.distribucionTipoTarea?.length > 0 && (
              <Box sx={{ flexGrow: 1 }}>
                <Box display="flex" alignItems="center" mb={1}>
                  <Assignment
                    sx={{ color: "primary.main", mr: 1, fontSize: 18 }}
                  />
                  <Typography
                    variant="body2"
                    color="textSecondary"
                    fontWeight="medium"
                  >
                    Top 3 Tipos de Tareas
                  </Typography>
                </Box>
                <Stack spacing={1}>
                  {data.distribucionTipoTarea.slice(0, 3).map((item, index) => (
                    <Box
                      key={index}
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                      sx={{
                        p: 1,
                        bgcolor: "background.neutral",
                        borderRadius: 1,
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{ fontSize: 11, flex: 1 }}
                      >
                        {item.tipo.length > 20
                          ? `${item.tipo.substring(0, 20)}...`
                          : item.tipo}
                      </Typography>
                      <Chip
                        label={fNumber(item.cantidad)}
                        size="small"
                        color="primary"
                        sx={{ minWidth: 50, height: 20, fontSize: 10 }}
                      />
                    </Box>
                  ))}
                </Stack>
              </Box>
            )}
          </Stack>
        </Grid>

        {/* Columna derecha - Gráficos */}
        <Grid item xs={12} lg={7}>
          <Stack spacing={2}>
            {/* Gráfico de tendencias semanales */}
            {data?.tendenciasSemanales?.length > 0 && (
              <Box>
                <Typography
                  variant="subtitle2"
                  gutterBottom
                  color="textSecondary"
                  fontWeight="medium"
                >
                  Tendencia Semanal
                </Typography>
                <Box sx={{ height: { xs: 200, sm: 250 } }}>
                  <Chart
                    options={chartOptions}
                    series={series}
                    type="line"
                    height="100%"
                  />
                </Box>
              </Box>
            )}

            {/* Gráfico donut de distribución */}
            {distribucionData.series.length > 0 && (
              <Box>
                <Typography
                  variant="subtitle2"
                  gutterBottom
                  color="textSecondary"
                  fontWeight="medium"
                >
                  Distribución por Tipo
                </Typography>
                <Box sx={{ height: { xs: 250, sm: 300 } }}>
                  <Chart
                    options={donutOptions}
                    series={distribucionData.series}
                    type="donut"
                    height="100%"
                  />
                </Box>
              </Box>
            )}
          </Stack>
        </Grid>
      </Grid>
    </Card>
  );
}
